import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { CONFIG } from 'variables/config';
import { MdClose, MdFileDownload } from 'react-icons/md';

// Bouton « Exporter » de l'onglet « En cours » : CSV des coordonnées des leads importés chez Batiweb
// (date de génération + contact, sans les prix), filtré par statut Batiweb et par industrie / produit.

type ExportStatus = 'pending' | 'sold' | 'unsold';

interface ExportRow {
    _id: string;
    createdAt: string;
    contact: {
        firstname: string | null;
        lastname: string | null;
        email: string | null;
        phone: string | null;
        address: string | null;
        zipcode: string | null;
        city: string | null;
    };
    industry: string | null;
    subIndustry: string | null;
}

const STATUS_OPTIONS: { key: ExportStatus; label: string }[] = [
    { key: 'pending', label: 'En attente' },
    { key: 'sold', label: 'Vendu' },
    { key: 'unsold', label: 'Refusé' },
];

// Clé d'un périmètre : « industrie:: » = industrie entière, « industrie::produit » = un produit.
const scopeKey = (industry: string, subIndustry: string | null = null) => `${industry}::${subIndustry ?? ''}`;

const CSV_HEADERS = ['Date de génération', 'Prénom', 'Nom', 'Email', 'Téléphone', 'Adresse', 'Code postal', 'Ville', 'Industrie', 'Produit'];

const toCsv = (rows: ExportRow[]) => {
    const lines = rows.map((row) => [
        new Date(row.createdAt).toLocaleString('fr-FR'),
        row.contact.firstname,
        row.contact.lastname,
        row.contact.email,
        row.contact.phone,
        row.contact.address,
        row.contact.zipcode,
        row.contact.city,
        row.industry,
        row.subIndustry,
    ]);
    // BOM : Excel lit sinon les accents de travers.
    return '﻿' + [CSV_HEADERS, ...lines].map((line) => line.map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
};

interface Props {
    onClose: () => void;
}

export default function ExportLeadsModal({ onClose }: Props) {
    const [statuses, setStatuses] = useState<Set<ExportStatus>>(new Set(['pending', 'sold', 'unsold']));
    const [catalog, setCatalog] = useState<{ industry: string; subIndustry: string }[]>([]);
    const [selected, setSelected] = useState<Set<string>>(new Set());
    const [search, setSearch] = useState('');
    const [excludeValidated, setExcludeValidated] = useState(true);
    const [excludeDays, setExcludeDays] = useState(30);
    const [exporting, setExporting] = useState(false);
    const [error, setError] = useState('');
    const [result, setResult] = useState('');

    useEffect(() => {
        axios
            .get(`${CONFIG.apiUrl}/admin/batiweb/matching`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
            .then((res) => setCatalog(res.data.rows.map((r: any) => ({ industry: r.industry, subIndustry: r.subIndustry }))))
            .catch(() => setError('Impossible de charger la liste des industries.'));
    }, []);

    // Produits du catalogue groupés par industrie ; la recherche porte sur l'industrie et le produit.
    const groups = useMemo(() => {
        const q = search.trim().toLowerCase();
        const byIndustry = new Map<string, string[]>();
        for (const row of catalog) {
            const industryMatches = row.industry.toLowerCase().includes(q);
            if (q && !industryMatches && !row.subIndustry.toLowerCase().includes(q)) continue;
            byIndustry.set(row.industry, [...(byIndustry.get(row.industry) || []), row.subIndustry]);
        }
        return [...byIndustry.entries()].sort(([a], [b]) => a.localeCompare(b, 'fr'));
    }, [catalog, search]);

    const toggle = <T,>(set: Set<T>, value: T) => {
        const next = new Set(set);
        next.has(value) ? next.delete(value) : next.add(value);
        return next;
    };

    // Cocher une industrie entière remplace les produits cochés un par un dans celle-ci.
    const toggleIndustry = (industry: string) =>
        setSelected((prev) => {
            const next = toggle(prev, scopeKey(industry));
            [...next].forEach((key) => key.startsWith(scopeKey(industry)) && key !== scopeKey(industry) && next.delete(key));
            return next;
        });

    const selectedLabels = [...selected].map((key) => {
        const [industry, subIndustry] = key.split('::');
        return subIndustry || `${industry} (toute l'industrie)`;
    });

    const handleExport = async () => {
        setError('');
        setResult('');
        try {
            setExporting(true);
            const scopes = [...selected].map((key) => {
                const [industry, subIndustry] = key.split('::');
                return { industry, subIndustry: subIndustry || null };
            });
            const res = await axios.post(
                `${CONFIG.apiUrl}/admin/batiweb/imported-leads/export`,
                { statuses: [...statuses], scopes, excludeValidatedDays: excludeValidated ? excludeDays : null },
                { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
            );
            const rows: ExportRow[] = res.data.leads;
            const excluded = excludeValidated
                ? ` · ${res.data.excluded} exclu(s) (validés par Batiweb depuis moins de ${excludeDays} jour(s), ou même contact)`
                : '';
            if (rows.length === 0) {
                setResult(`Aucun lead à exporter${excluded}.`);
                return;
            }
            const url = URL.createObjectURL(new Blob([toCsv(rows)], { type: 'text/csv;charset=utf-8;' }));
            const link = document.createElement('a');
            link.setAttribute('href', url);
            link.setAttribute('download', `leads_batiweb_${new Date().toISOString().slice(0, 10)}.csv`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            setResult(`${rows.length} lead(s) exporté(s)${excluded}.`);
        } catch (e: any) {
            setError(e.response?.data?.message || "Erreur lors de l'export.");
        } finally {
            setExporting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center">
            <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose} />
            <div className="relative z-10 m-4 flex max-h-[90vh] w-full max-w-2xl flex-col rounded-lg bg-white shadow-xl dark:bg-navy-800">
                <div className="flex items-start justify-between border-b border-gray-200 px-6 py-4 dark:border-navy-600">
                    <div>
                        <h2 className="text-lg font-bold text-navy-700 dark:text-white">Exporter les leads</h2>
                        <p className="mt-1 text-sm text-gray-500">
                            CSV : date de génération, prénom, nom, email, téléphone, adresse, code postal, ville, industrie et produit.
                        </p>
                    </div>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
                        <MdClose size={22} />
                    </button>
                </div>

                <div className="space-y-4 border-b border-gray-200 px-6 py-4 dark:border-navy-600">
                    <div>
                        <div className="mb-1 text-xs font-bold uppercase text-gray-500">Statut Batiweb</div>
                        <div className="flex gap-4">
                            {STATUS_OPTIONS.map((option) => (
                                <label key={option.key} className="flex items-center gap-2 text-sm text-navy-700 dark:text-white">
                                    <input type="checkbox" checked={statuses.has(option.key)} onChange={() => setStatuses((prev) => toggle(prev, option.key))} />
                                    {option.label}
                                </label>
                            ))}
                        </div>
                    </div>
                    <label className="flex flex-wrap items-center gap-2 text-sm text-navy-700 dark:text-white">
                        <input type="checkbox" checked={excludeValidated} onChange={(e) => setExcludeValidated(e.target.checked)} />
                        Exclure les leads validés par Batiweb dans les
                        <input
                            type="number"
                            min={1}
                            value={excludeDays}
                            disabled={!excludeValidated}
                            onChange={(e) => setExcludeDays(Math.max(1, Number(e.target.value) || 1))}
                            className="w-20 rounded-lg border border-gray-300 bg-white px-2 py-1 text-sm disabled:opacity-40 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                        />
                        derniers jours
                        <span className="w-full text-xs text-gray-400">Exclut aussi les autres leads de la même personne (même email ou téléphone).</span>
                    </label>
                </div>

                <div className="border-b border-gray-200 px-6 py-3 dark:border-navy-600">
                    <div className="mb-2 flex items-center justify-between">
                        <div className="text-xs font-bold uppercase text-gray-500">Industrie / produit</div>
                        {selected.size > 0 && (
                            <button onClick={() => setSelected(new Set())} className="text-xs font-semibold text-brand-500 hover:underline">
                                Tout effacer
                            </button>
                        )}
                    </div>
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Rechercher une industrie ou un produit (ex. panneaux solaires)"
                        className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                    />
                    <div className="mt-2 text-xs text-gray-500">
                        {selected.size === 0 ? 'Aucune sélection : toutes les industries.' : `Sélection : ${selectedLabels.join(', ')}`}
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto px-6 py-3">
                    {groups.length === 0 && <div className="py-6 text-center text-sm text-gray-400">Aucune industrie.</div>}
                    {groups.map(([industry, products]) => {
                        const wholeIndustry = selected.has(scopeKey(industry));
                        return (
                            <div key={industry} className="mb-3">
                                <label className="flex cursor-pointer items-center gap-3 rounded px-2 py-1.5 text-sm font-semibold hover:bg-gray-50 dark:hover:bg-navy-700">
                                    <input type="checkbox" checked={wholeIndustry} onChange={() => toggleIndustry(industry)} />
                                    <span className="text-navy-700 dark:text-white">{industry}</span>
                                    <span className="text-xs font-normal text-gray-400">toute l'industrie</span>
                                </label>
                                {products.map((product) => (
                                    <label key={product} className="ml-6 flex cursor-pointer items-center gap-3 rounded px-2 py-1 text-sm hover:bg-gray-50 dark:hover:bg-navy-700">
                                        <input
                                            type="checkbox"
                                            checked={wholeIndustry || selected.has(scopeKey(industry, product))}
                                            disabled={wholeIndustry}
                                            onChange={() => setSelected((prev) => toggle(prev, scopeKey(industry, product)))}
                                        />
                                        <span className="text-navy-700 dark:text-white">{product}</span>
                                    </label>
                                ))}
                            </div>
                        );
                    })}
                </div>

                <div className="border-t border-gray-200 px-6 py-4 dark:border-navy-600">
                    {error && <div className="mb-3 rounded-lg bg-red-100 p-2 text-sm text-red-700">{error}</div>}
                    {result && <div className="mb-3 rounded-lg bg-green-100 p-2 text-sm text-green-700">{result}</div>}
                    <div className="flex justify-end">
                        <button
                            onClick={handleExport}
                            disabled={statuses.size === 0 || exporting}
                            className="flex items-center gap-1 rounded-lg bg-brand-500 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600 disabled:opacity-40"
                        >
                            <MdFileDownload />
                            {exporting ? 'Export...' : 'Exporter en CSV'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
